import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery } from 'convex/react'
import { useAuthActions } from '@convex-dev/auth/react'
import { Rocket, LogOut, ExternalLink } from 'lucide-react'
import { api } from '../lib/api'
import type { Doc, Id } from '../lib/api'
import { PUBLIC_ORIGIN, publicPageUrl } from '../lib/config'
import { draftToPublicPage, type DraftPatch } from '../lib/draftToPublicPage'
import { useDebouncedCallback } from '../lib/useDebouncedCallback'
import { useUpload } from '../lib/useUpload'
import { Spinner } from './ui/Spinner'
import { PagePreview } from './ui/PagePreview'
import { PhoneFrame } from './ui/PhoneFrame'
import { PageTab } from './editor/PageTab'
import { StyleTab } from './editor/StyleTab'
import { StatsTab } from './editor/StatsTab'
import { SettingsTab } from './editor/SettingsTab'
import { ClaimPrompt } from './editor/ClaimPrompt'
import { DeployModal } from './editor/DeployModal'
import { BrandLogo } from './ui/BrandLogo'

type Draft = Doc<'pages'>['draft']
type Tab = 'page' | 'style' | 'stats' | 'settings'

const TABS: { id: Tab; label: string }[] = [
  { id: 'page', label: 'Page' },
  { id: 'style', label: 'Style' },
  { id: 'stats', label: 'Stats' },
  { id: 'settings', label: 'Settings' },
]

export function Editor() {
  const data = useQuery(api.pages.getMyPage)
  const claimPage = useMutation(api.pages.claimPage)
  const updateDraft = useMutation(api.pages.updateDraft)
  const addStartupMut = useMutation(api.pageStartups.add)
  const updateStartupMut = useMutation(api.pageStartups.update)
  const removeStartupMut = useMutation(api.pageStartups.remove)
  const reorderMut = useMutation(api.pageStartups.reorder)
  const publish = useMutation(api.pages.publishPage)
  const { signOut } = useAuthActions()
  const { upload } = useUpload()
  const [params, setParams] = useSearchParams()

  const [tab, setTab] = useState<Tab>('page')
  const [localDraft, setLocalDraft] = useState<Draft | null>(null)
  const [deployUrl, setDeployUrl] = useState<string | null>(null)
  const [deploying, setDeploying] = useState(false)
  const [needsPayment, setNeedsPayment] = useState(false)
  const claimedRef = useRef(false)

  // Auto-claim from the ?claim handoff (set on the landing page).
  useEffect(() => {
    const claim = params.get('claim')
    if (data === null && claim && !claimedRef.current) {
      claimedRef.current = true
      claimPage({ username: claim })
        .catch(() => {})
        .finally(() => {
          params.delete('claim')
          setParams(params, { replace: true })
        })
    }
  }, [data, params, claimPage, setParams])

  // Initialize local draft once the page loads.
  useEffect(() => {
    if (data?.page && localDraft === null) setLocalDraft(data.page.draft)
  }, [data, localDraft])

  const saveDraft = useDebouncedCallback((patch: Record<string, unknown>) => {
    updateDraft(patch).catch(() => {})
  }, 600)

  const onDraftChange = useCallback(
    (patch: DraftPatch) => {
      setLocalDraft((prev) => {
        if (!prev) return prev
        const next: Draft = {
          ...prev,
          ...patch,
          avatarStorageId:
            patch.avatarStorageId === null
              ? undefined
              : patch.avatarStorageId ?? prev.avatarStorageId,
          social: patch.social ? { ...prev.social, ...patch.social } : prev.social,
          emailCollection: patch.emailCollection
            ? { ...prev.emailCollection, ...patch.emailCollection }
            : prev.emailCollection,
        }
        return next
      })
      saveDraft(patch as Record<string, unknown>)
    },
    [saveDraft],
  )

  // Loading state.
  if (data === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-zinc-950">
        <Spinner />
      </div>
    )
  }

  // No page yet → claim (unless a ?claim is mid-flight).
  if (data === null) {
    if (params.get('claim')) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-zinc-950">
          <Spinner />
        </div>
      )
    }
    return <ClaimPrompt onClaim={async (username) => void (await claimPage({ username }))} />
  }

  const { page, startups, avatarUrl, entitled } = data
  const draft = localDraft ?? page.draft
  const username = page.usernameDisplay || page.username
  const preview = draftToPublicPage(page.username, draft, avatarUrl, startups)

  async function onUploadAvatar(file: File) {
    const storageId = await upload(file)
    onDraftChange({ avatarStorageId: storageId })
  }

  async function onDeploy() {
    if (!entitled) {
      setNeedsPayment(true)
      return
    }
    setDeploying(true)
    try {
      const res = await publish()
      setDeployUrl(publicPageUrl(res.username))
    } catch (e) {
      if (e instanceof Error && e.message.includes('PAYMENT_REQUIRED')) setNeedsPayment(true)
    } finally {
      setDeploying(false)
    }
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      {/* Top bar */}
      <header className="sticky top-0 z-20 border-b border-zinc-800 bg-zinc-950/90 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-3">
          <div className="flex min-w-0 items-center gap-4">
            <a href={PUBLIC_ORIGIN} className="editor-brand flex-shrink-0 text-white no-underline">
              <BrandLogo />
            </a>
            <div className="flex min-w-0 items-center gap-1 overflow-x-auto rounded-lg bg-zinc-900 p-1">
              {TABS.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={`rounded-md px-3 py-1.5 text-sm font-semibold transition ${
                    tab === t.id ? 'bg-zinc-700 text-white' : 'text-zinc-400 hover:text-white'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {page.status === 'published' && (
              <a
                href={publicPageUrl(page.username)}
                target="_blank"
                rel="noopener noreferrer"
                className="hidden items-center gap-1 rounded-lg border border-zinc-700 px-3 py-2 text-sm text-zinc-300 hover:text-white sm:flex"
              >
                View live <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
            <button
              onClick={onDeploy}
              disabled={deploying}
              className="flex items-center gap-2 rounded-lg bg-fuchsia-500 px-4 py-2 text-sm font-bold text-white hover:bg-fuchsia-400 disabled:opacity-50"
            >
              {deploying ? <Spinner className="h-4 w-4" /> : <Rocket className="h-4 w-4" />}
              Deploy
            </button>
            <button
              onClick={() => signOut()}
              className="rounded-lg p-2 text-zinc-500 hover:text-white"
              aria-label="Log out"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      {/* Split layout: editor | preview */}
      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-8 px-4 py-6 lg:grid-cols-[1fr_400px]">
        <div>
          {tab === 'page' && (
            <PageTab
              draft={draft}
              avatarUrl={avatarUrl}
              startups={startups}
              onDraftChange={onDraftChange}
              onUploadAvatar={onUploadAvatar}
              onAddStartup={() => addStartupMut({ name: 'New startup' })}
              onSaveStartup={(id: Id<'pageStartups'>, patch) =>
                updateStartupMut({ startupId: id, ...patch })
              }
              onRemoveStartup={(id: Id<'pageStartups'>) => removeStartupMut({ startupId: id })}
              onReorder={(orderedIds: Id<'pageStartups'>[]) => reorderMut({ orderedIds })}
            />
          )}
          {tab === 'style' && (
            <StyleTab
              theme={draft.theme}
              font={draft.font}
              onThemeChange={(id) => onDraftChange({ theme: id })}
              onFontChange={(id) => onDraftChange({ font: id })}
            />
          )}
          {tab === 'stats' && <StatsTab />}
          {tab === 'settings' && <SettingsTab page={page} />}
        </div>

        {/* Live preview */}
        <div className="lg:sticky lg:top-20 lg:self-start">
          <PhoneFrame footer={`Preview · ${username}`}>
            <PagePreview page={preview} phone />
          </PhoneFrame>
        </div>
      </div>

      {deployUrl && <DeployModal url={deployUrl} onClose={() => setDeployUrl(null)} />}
      {needsPayment && <PaymentRequired onClose={() => setNeedsPayment(false)} />}
    </div>
  )
}

/** Lightweight upsell shown when an unentitled user hits Deploy. */
function PaymentRequired({ onClose }: { onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
      <div className="w-full max-w-md rounded-2xl border border-zinc-800 bg-zinc-900 p-6 text-center">
        <h2 className="text-xl font-extrabold text-white">One step to go live</h2>
        <p className="mt-2 text-sm text-zinc-400">
          Unlock publishing with a one-time pass. Edit and preview for free — pay once to deploy.
        </p>
        <a
          href="/pricing"
          className="mt-5 flex w-full items-center justify-center rounded-xl bg-fuchsia-500 py-3 text-sm font-bold text-white hover:bg-fuchsia-400"
        >
          See pricing
        </a>
        <button onClick={onClose} className="mt-3 text-xs text-zinc-500 hover:text-zinc-300">
          Keep editing
        </button>
      </div>
    </div>
  )
}
